/**
 * Export and Share Modal Component
 * Provides UI for exporting conversations and creating shareable links
 * Validates: Requirements 22.1, 22.2, 22.3
 */
import { useState, useEffect } from 'react';
import apiClient from '../../api/client';

interface ExportShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  sessionTitle: string | null;
}

type ExportFormat = 'markdown' | 'json';

interface ShareInfo {
  id: string;
  shareToken: string;
  shareUrl: string;
  expiresAt: string | null;
  viewCount: number;
}

export function ExportShareModal({
  isOpen,
  onClose,
  sessionId,
  sessionTitle,
}: ExportShareModalProps) {
  const [activeTab, setActiveTab] = useState<'export' | 'share'>('export');
  const [exportFormat, setExportFormat] = useState<ExportFormat>('markdown');
  const [isExporting, setIsExporting] = useState(false);
  const [shareInfo, setShareInfo] = useState<ShareInfo | null>(null);
  const [isLoadingShare, setIsLoadingShare] = useState(false);
  const [expiresInHours, setExpiresInHours] = useState<number>(0); // 0 = never
  const [copySuccess, setCopySuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing share info when modal opens
  useEffect(() => {
    if (isOpen && sessionId) {
      loadShareInfo();
    }
  }, [isOpen, sessionId]);

  const loadShareInfo = async () => {
    try {
      setIsLoadingShare(true);
      const response = await apiClient.get(`/api/share/session/${sessionId}`);
      setShareInfo(response.data.data);
    } catch (err) {
      console.error('Failed to load share info:', err);
    } finally {
      setIsLoadingShare(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      setError(null);

      // Fetch the export content
      const response = await apiClient.get(`/api/export/${sessionId}`, {
        params: { format: exportFormat },
        responseType: 'blob',
      });

      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `conversation.${exportFormat === 'markdown' ? 'md' : 'json'}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
          filename = match[1];
        }
      }

      // Create download link
      const blob = new Blob([response.data], {
        type: exportFormat === 'markdown' ? 'text/markdown' : 'application/json',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Close modal after successful export
      onClose();
    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to export conversation. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleCreateShare = async () => {
    try {
      setIsLoadingShare(true);
      setError(null);

      const response = await apiClient.post('/api/share', {
        sessionId,
        expiresInHours: expiresInHours > 0 ? expiresInHours : undefined,
      });

      setShareInfo(response.data.data);
    } catch (err) {
      console.error('Failed to create share:', err);
      setError('Failed to create share link. Please try again.');
    } finally {
      setIsLoadingShare(false);
    }
  };

  const handleDeleteShare = async () => {
    if (!shareInfo) return;

    try {
      setIsLoadingShare(true);
      setError(null);

      await apiClient.delete(`/api/share/${shareInfo.id}`);
      setShareInfo(null);
    } catch (err) {
      console.error('Failed to delete share:', err);
      setError('Failed to delete share link. Please try again.');
    } finally {
      setIsLoadingShare(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareInfo) return;

    const fullUrl = `${window.location.origin}${shareInfo.shareUrl}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-gray-900 rounded-xl shadow-2xl w-full max-w-md mx-4 border border-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <h2 className="text-lg font-semibold text-white">
            {sessionTitle || 'Conversation'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-800">
          <button
            onClick={() => setActiveTab('export')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'export'
                ? 'text-primary-400 border-b-2 border-primary-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export
            </span>
          </button>
          <button
            onClick={() => setActiveTab('share')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'share'
                ? 'text-primary-400 border-b-2 border-primary-400'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Share
            </span>
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-800 rounded-lg text-sm text-red-400">
              {error}
            </div>
          )}

          {activeTab === 'export' ? (
            <ExportTab
              format={exportFormat}
              onFormatChange={setExportFormat}
              onExport={handleExport}
              isExporting={isExporting}
            />
          ) : (
            <ShareTab
              shareInfo={shareInfo}
              isLoading={isLoadingShare}
              expiresInHours={expiresInHours}
              onExpiresChange={setExpiresInHours}
              onCreateShare={handleCreateShare}
              onDeleteShare={handleDeleteShare}
              onCopyLink={handleCopyLink}
              copySuccess={copySuccess}
            />
          )}
        </div>
      </div>
    </div>
  );
}

interface ExportTabProps {
  format: ExportFormat;
  onFormatChange: (format: ExportFormat) => void;
  onExport: () => void;
  isExporting: boolean;
}

function ExportTab({ format, onFormatChange, onExport, isExporting }: ExportTabProps) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Export Format
        </label>
        <div className="grid grid-cols-2 gap-3">
          <FormatOption
            format="markdown"
            label="Markdown"
            description="Human-readable format"
            icon="📝"
            isSelected={format === 'markdown'}
            onSelect={() => onFormatChange('markdown')}
          />
          <FormatOption
            format="json"
            label="JSON"
            description="Machine-readable format"
            icon="{ }"
            isSelected={format === 'json'}
            onSelect={() => onFormatChange('json')}
          />
        </div>
      </div>

      <button
        onClick={onExport}
        disabled={isExporting}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
      >
        {isExporting ? (
          <>
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Exporting...
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download {format === 'markdown' ? '.md' : '.json'}
          </>
        )}
      </button>
    </div>
  );
}

interface FormatOptionProps {
  format: ExportFormat;
  label: string;
  description: string;
  icon: string;
  isSelected: boolean;
  onSelect: () => void;
}

function FormatOption({ label, description, icon, isSelected, onSelect }: FormatOptionProps) {
  return (
    <button
      onClick={onSelect}
      className={`p-3 rounded-lg border text-left transition-colors ${
        isSelected
          ? 'border-primary-500 bg-primary-900/30'
          : 'border-gray-700 hover:border-gray-600 bg-gray-800/50'
      }`}
    >
      <div className="text-lg mb-1">{icon}</div>
      <div className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-gray-300'}`}>
        {label}
      </div>
      <div className="text-xs text-gray-500">{description}</div>
    </button>
  );
}

interface ShareTabProps {
  shareInfo: ShareInfo | null;
  isLoading: boolean;
  expiresInHours: number;
  onExpiresChange: (hours: number) => void;
  onCreateShare: () => void;
  onDeleteShare: () => void;
  onCopyLink: () => void;
  copySuccess: boolean;
}

function ShareTab({
  shareInfo,
  isLoading,
  expiresInHours,
  onExpiresChange,
  onCreateShare,
  onDeleteShare,
  onCopyLink,
  copySuccess,
}: ShareTabProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <svg className="w-6 h-6 animate-spin text-primary-400" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (shareInfo) {
    const fullUrl = `${window.location.origin}${shareInfo.shareUrl}`;
    const isExpired = shareInfo.expiresAt && new Date(shareInfo.expiresAt) < new Date();

    return (
      <div className="space-y-4">
        {isExpired && (
          <div className="p-3 bg-yellow-900/30 border border-yellow-800 rounded-lg text-sm text-yellow-400">
            This share link has expired
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Share Link
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={fullUrl}
              readOnly
              className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-300 truncate"
            />
            <button
              onClick={onCopyLink}
              className={`px-3 py-2 rounded-lg transition-colors ${
                copySuccess
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-700 hover:bg-gray-600 text-white'
              }`}
            >
              {copySuccess ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-400">
            {shareInfo.viewCount} view{shareInfo.viewCount !== 1 ? 's' : ''}
          </span>
          {shareInfo.expiresAt && (
            <span className={isExpired ? 'text-red-400' : 'text-gray-400'}>
              {isExpired ? 'Expired' : `Expires ${formatExpiration(shareInfo.expiresAt)}`}
            </span>
          )}
        </div>

        <button
          onClick={onDeleteShare}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-900/30 hover:bg-red-900/50 border border-red-800 text-red-400 rounded-lg transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
          Remove Share Link
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-400">
        Create a shareable link to let others view this conversation in read-only mode.
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Link Expiration
        </label>
        <select
          value={expiresInHours}
          onChange={(e) => onExpiresChange(Number(e.target.value))}
          className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-300"
        >
          <option value={0}>Never expires</option>
          <option value={1}>1 hour</option>
          <option value={24}>24 hours</option>
          <option value={168}>7 days</option>
          <option value={720}>30 days</option>
        </select>
      </div>

      <button
        onClick={onCreateShare}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        </svg>
        Create Share Link
      </button>
    </div>
  );
}

function formatExpiration(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = date.getTime() - now.getTime();

  if (diff < 0) return 'expired';

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (days > 0) return `in ${days} day${days > 1 ? 's' : ''}`;
  if (hours > 0) return `in ${hours} hour${hours > 1 ? 's' : ''}`;
  return 'soon';
}
